import { getData, postData, postFormData, putData, deleteData, patchData } from "./rest";
import { objectToQueryString } from "../../utils/api";
import { ListParameters } from "common/build/api-parameters/common";
import { MappedStoryQuestion } from "common/build/prisma/client/client";

// Generic API response wrapper
export interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    error?: string;
}

// Card color enum for V4 template features
export type CardColour =
    | "Red"
    | "Orange"
    | "Yellow"
    | "Green"
    | "Cyan"
    | "Blue"
    | "Magneta"
    | "Crimson"
    | "Silver"
    | "Bronze"
    | "BrightYellow"
    | "Aqua"
    | "Purple"
    | "BrightOrange"
    | "BrightRed"
    | "LightGreen"
    | "NightBlue";

// Types for branched story API
export interface BranchedStoryType {
    id: number;
    storyName: string;
    author: string;
    synopsis?: string;
    type: "BranchedStory";
    isPublished: boolean;
    isReviewed?: boolean;
    createdDate: string;
    lastModified?: string;
    maxBranchDepth?: number;
    totalDecisionPoints?: number;
    totalPaths?: number;
    maxDepth?: number;
    // V4 Template fields - optional for backward compatibility
    ageRange?: string;
    colour?: CardColour;
    resources?: string; // NI Link
    resourcesROI?: string; // ROI Link
    reviewedDate?: string;
    CreateUser?: {
        id: number;
        firstName?: string;
        lastName?: string;
        email?: string;
    };
    ReviewUser?: {
        id: number;
        firstName?: string;
        lastName?: string;
        email?: string;
    };
    Themes?: Array<{
        id: number;
        name: string;
    }>;
    StoryDecisionPoints?: Array<{
        id: number;
        decisionText: string;
        order: number;
        branches: Array<{
            id: number;
            branchLabel: string;
            choiceText: string;
            targetPartNumber: number;
            order: number;
        }>;
    }>;
    StoryParts?: Array<{
        id: number;
        part: number;
        content: string;
        partLabel?: string;
        StoryDecisionPoints?: Array<{
            id: number;
            decisionText: string;
            order: number;
            branches: Array<{
                id: number;
                branchLabel: string;
                choiceText: string;
                targetPartNumber: number;
                order: number;
            }>;
        }>;
        MappedStoryQuestion?: Array<Partial<MappedStoryQuestion>>;
    }>;
    [key: string]: unknown;
}

export interface BranchedStoryListParameters extends ListParameters {
    filterByPublished?: boolean;
    filterByTheme?: number;
    [key: string]: unknown;
}

export interface CreateBranchedStoryData {
    storyName: string;
    author: string;
    synopsis?: string;
    themes?: number[];
    isPublished?: boolean;
    // V4 Template fields - optional for backward compatibility
    ageRange?: string;
    colour?: CardColour;
    resources?: string;
    resourcesROI?: string;
}

export interface UpdateBranchedStoryData {
    storyName?: string;
    author?: string;
    synopsis?: string;
    themes?: number[];
    isPublished?: boolean;
    // V4 Template fields - optional for backward compatibility
    ageRange?: string;
    colour?: CardColour;
    resources?: string;
    resourcesROI?: string;
    // Complex update fields for nested entities
    storyParts?: Array<{
        id: number;
        content?: string;
        partLabel?: string;
    }>;
    // New parts to create (from addStoryPart)
    newParts?: Array<{
        part: number;
        content: string;
        partLabel?: string;
    }>;
    // New questions to create
    newQuestions?: Array<Partial<MappedStoryQuestion>>;
    // New decision points to create
    newDecisionPoints?: Array<{
        storyPartId: number;
        decisionText: string;
        order: number;
        branches: Array<{
            branchLabel: string;
            choiceText: string;
            targetPartNumber: number;
            order: number;
        }>;
    }>;
    decisionPoints?: Array<{
        id: number;
        decisionText?: string;
        order?: number;
        branches?: Array<{
            id: number;
            branchLabel?: string;
            choiceText?: string;
            targetPartNumber?: number;
            order?: number;
        }>;
    }>;
    questions?: Array<Partial<MappedStoryQuestion>>;
}

export interface UploadDocxStoryData {
    file: File;
}

export interface BranchedStoryUploadResult {
    success: boolean;
    storyId?: number;
    validationResult: ValidationResult;
    error?: string;
    // Full parsed story for review/edit in wizard (before saving to DB)
    parsedStory?: ParsedStoryStructure;
}

export interface ParsedStoryStructure {
    title: string;
    author: string;
    date?: string;
    version?: string;
    parts: ParsedStoryPart[];
    maxDepth: number;
    fileName?: string;
    synopsis?: string;
    ageRange?: string;
    resources?: string;
    resourcesROI?: string;
    colour?: string;
    themes?: string;
    imageUrls?: string[];
    parsingWarnings?: string[];
}

export interface ParsedStoryPart {
    partNumber: number;
    content: string;
    partLabel?: string;
    decisionPoints?: ParsedDecisionPoint[];
    questions?: Partial<MappedStoryQuestion>[];
    parseConfidence?: number; // 0.0-1.0, undefined for template-parsed (= 1.0)
}

export interface ParsedDecisionPoint {
    decisionText: string;
    order: number;
    branches: ParsedBranch[];
}

export interface ParsedBranch {
    branchLabel: string;
    choiceText: string;
    targetPartNumber: number;
    order: number;
}

export interface ValidationResult {
    valid: boolean;
    errors: Array<{
        type: "error" | "warning";
        message: string;
    }>;
    warnings: Array<{
        type: "error" | "warning";
        message: string;
    }>;
    stats: {
        totalParts: number;
        totalDecisionPoints: number;
        totalBranches: number;
        maxDepth: number;
        endPoints: number;
        // V4 Template specific stats - optional for backward compatibility
        totalQuestions?: number;
        questionsWithPrompts?: number;
        questionsWithKeywords?: number;
        questionsWithHints?: number;
        hasAgeRange?: boolean;
        hasResourceLinks?: boolean;
        // V5 DOCX specific stats
        imageCount?: number;
    };
}

export interface StoryStructure {
    storyId: number;
    title: string;
    author: string;
    maxDepth: number;
    totalParts: number;
    entryPoint: StoryNavigationNode;
    allParts: StoryNavigationNode[];
}

export interface StoryNavigationNode {
    partNumber: number;
    content: string;
    partLabel?: string;
    questions?: Array<Partial<MappedStoryQuestion>>;
    decisionPoints?: Array<{
        decisionText: string;
        order: number;
        branches: Array<{
            branchLabel: string;
            choiceText: string;
            targetPartNumber: number;
            order: number;
        }>;
    }>;
    isEndNode: boolean;
}

// Check if a story name already exists
export const checkNameExists = async (name: string): Promise<{ exists: boolean; storyId?: number }> => {
    return await getData(`/branched-story/check-name?name=${encodeURIComponent(name)}`);
};

// API methods following existing patterns
export const list = async (
    params?: BranchedStoryListParameters,
): Promise<{ items: BranchedStoryType[]; total: number }> => {
    const queryString = params ? objectToQueryString(params) : "";
    return await getData(`/branched-story${queryString}`);
};

export const getById = async (id: number): Promise<BranchedStoryType> => {
    return await getData(`/branched-story/${id}`);
};

export const get = async (id: number, args: Record<string, unknown>): Promise<BranchedStoryType> => {
    const queryString = Object.keys(args).length > 0 ? objectToQueryString(args) : "";
    return await getData(`/branched-story/get/${id}${queryString}`);
};

export const getItem = async (id: number, args: Record<string, unknown>): Promise<BranchedStoryType> => {
    const queryString = Object.keys(args).length > 0 ? objectToQueryString(args) : "";
    return await getData(`/branched-story/${id}${queryString}`);
};

export const create = async (data: CreateBranchedStoryData): Promise<Response> => {
    return await postData("/branched-story", data);
};

export const update = async (id: number, data: UpdateBranchedStoryData): Promise<Response> => {
    return await putData(`/branched-story/${id}`, data);
};

export const remove = async (id: number): Promise<Response> => {
    return await deleteData(`/branched-story/${id}`);
};

// V5 multipart file upload (supports .docx)
export const uploadFile = async (file: File): Promise<BranchedStoryUploadResult> => {
    const formData = new FormData();
    formData.append("file", file);

    // Use consistent API pattern with postFormData helper
    const response = await postFormData("/branched-story/upload", formData);

    if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
    }

    return await response.json();
};

// Wizard: Create story from parsed data (final step)
export const createFromWizard = async (
    parsedStory: ParsedStoryStructure,
    isPublished: boolean,
    themeIds?: number[],
): Promise<{ success: boolean; storyId?: number; error?: string }> => {
    const response = await postData("/branched-story/create-from-wizard", {
        parsedStory,
        isPublished,
        themeIds,
    });
    return await response.json();
};

export const validate = async (id: number): Promise<ValidationResult> => {
    const response = await postData(`/branched-story/${id}/validate`, {});
    return await response.json();
};

export const getStructure = async (storyId: number): Promise<StoryStructure> => {
    return await getData(`/branched-story/${storyId}/structure`);
};

export const navigateTopart = async (storyId: number, partNumber: number): Promise<StoryNavigationNode> => {
    return await getData(`/branched-story/${storyId}/navigate/${partNumber}`);
};

export const reviewStory = async (storyId: number): Promise<Response> =>
    await patchData(`/branched-story/${storyId}/review`);

export const publishStory = async (storyId: number): Promise<Response> =>
    await patchData(`/branched-story/${storyId}/publish`);
